import React, { useState } from "react";
import { Button } from "zudoku/ui/Button";
import { Card } from "zudoku/ui/Card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "zudoku/ui/Dialog";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "../components/ui/table";
import { Input } from "zudoku/ui/Input";
import { Head } from "zudoku/components";
import { useTenantStore } from "../stores/tenantStore";
import { useApplicationStore } from "../stores/useApplicationStore";
import useGetTenantApplications from "../APIs/useGetTenantApplications";
import useAddTenantApplication from "../APIs/useAddTenantApplication";
import useDeleteAppRegistration from "../APIs/useDeleteAppRegistration";
import useGetTenantApplication from "../APIs/useGetTenantApplication";
import useGetAPIDevelopers from "../APIs/useGetAPIDevelopers";
import { ArrowLeft } from "lucide-react";
import Link from "../Link";

export default function ApplicationsPage() {
  const { currentTenant, hasHydrated } = useTenantStore();
  const {data: tenants } = useGetAPIDevelopers();
  const { applicationId, setAppContext } = useApplicationStore();

  const { data, isFetching, error } = useGetTenantApplications();
  const { mutate: addApplication, isPending } = useAddTenantApplication();
  const { mutate: deleteAppRegistration, isPending: isDeleting } = useDeleteAppRegistration();

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [newAppName, setNewAppName] = useState("");
  const [applicationToDelete, setApplicationToDelete] = useState<any>(null);

  if (!tenants || tenants.length === 0) {
    return <AccessSettingsWrapper>You aren't a developer in any tenants</AccessSettingsWrapper>;
  }

  if (!hasHydrated) {
    return <AccessSettingsWrapper>Loading...</AccessSettingsWrapper>;
  }

  const tenant = tenants.find((t: any) => t.tenantCode.toLowerCase() === currentTenant);
  if (!tenant) {
    return <AccessSettingsWrapper>Tenant not found</AccessSettingsWrapper>;
  }

  const selectedTenantCode = tenant.tenantCode.toLowerCase();

  const handleDeleteApplication = (app: any) => {
    setApplicationToDelete(app);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (!applicationToDelete) return;
    deleteAppRegistration(applicationToDelete.id, {
      onSuccess: () => {
        setDeleteModalOpen(false);
        setApplicationToDelete(null);
      },
      onError: (err: Error) => {
        alert(`Failed to delete: ${err.message}`);
        setDeleteModalOpen(false);
      },
    });
  };

  const confirmAddApplication = () => {
    addApplication(newAppName);
    setNewAppName("");
    setAddModalOpen(false);
  };

  if (isFetching) return <AccessSettingsWrapper>Loading...</AccessSettingsWrapper>;
  if (isPending) return <AccessSettingsWrapper>Adding...</AccessSettingsWrapper>;
  if (isDeleting) return <AccessSettingsWrapper>Deleting...</AccessSettingsWrapper>;
  if (error) return <AccessSettingsWrapper>Error: {error.message}</AccessSettingsWrapper>;

  if (applicationId) {
    const { data: appData, isLoading: loadingApp, error: appError } = useGetTenantApplication(applicationId, currentTenant);

    if (loadingApp) return <AccessSettingsWrapper>Loading application...</AccessSettingsWrapper>;
    if (appError) return <AccessSettingsWrapper>Error loading application: {appError.message}</AccessSettingsWrapper>;

    return (
      <AccessSettingsWrapper>
        <Head>
          <title>{import.meta.env.ZUDOKU_PUBLIC_PAGE_TITLE} - Applications</title>
        </Head>
        <div className="flex items-center gap-2 mb-4">
          <Link to="/applications" className="text-blue-600 hover:underline flex items-center">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Applications
          </Link>
        </div>
        <h1 className="text-3xl font-bold mb-2">{appData.name}</h1>
        <p className="text-slate-700 mb-4">Client ID: {appData.clientId}</p>
      </AccessSettingsWrapper>
    );
  }

  return (
    <AccessSettingsWrapper>
      <Head>
        <title>{import.meta.env.ZUDOKU_PUBLIC_PAGE_TITLE} - Applications</title>
      </Head>
      <h1 className="text-3xl font-bold mb-2">{tenant?.name}</h1>
      <h2 className="text-xl text-muted-foreground mb-4">API Access Settings</h2>

      <div className="w-full">
        <p className="text-slate-600 mb-4 dark:text-slate-400">
          The following applications have API access for this tenant.
        </p>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Link</TableHead>
              <TableHead className="text-right pr-8">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data ?? []).map((app: any) => (
              <TableRow key={app.id} className="hover:bg-muted">
                <TableCell>{app.name}</TableCell>
                <TableCell>
                  <Link to={`/applications/${selectedTenantCode}/${app.id}`} onClick={() => setAppContext(selectedTenantCode, app.id)}>
                    Go to application settings
                  </Link>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteApplication(app);
                    }}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="mt-6">
          <Button className="bg-green-700 hover:bg-green-600" onClick={() => setAddModalOpen(true)}>
            Add an Application
          </Button>
        </div>
      </div>

      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add an Application</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label htmlFor="applicationName" className="font-bold block">
              {tenant?.name}
            </label>
            <Input
              id="applicationName"
              value={newAppName}
              onChange={(e) => setNewAppName(e.target.value)}
              placeholder="Enter application name"
            />
            <p className="text-sm text-muted-foreground">
              Give the application a distinct name to identify it.
            </p>
          </div>
          <DialogFooter>
            <Button variant="destructive" onClick={() => setAddModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmAddApplication}>Add Application</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Application</DialogTitle>
          </DialogHeader>
          <p>
            Are you sure you want to delete <span className="font-bold">{applicationToDelete?.name}</span>?
          </p>
          <DialogFooter>
            <Button variant="destructive" disabled={isDeleting} onClick={confirmDelete}>
              Delete
            </Button>
            <Button disabled={isDeleting} onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AccessSettingsWrapper>
  );
}

function AccessSettingsWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-full flex items-center flex-col py-6 px-6 lg:px-60 xl:px-80">
      <Card className="w-full shadow-md p-6">{children}</Card>
    </div>
  );
}
